import React from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Truck, SignOut, User, BookOpen } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const roleLabel = (() => {
    if (user?.role === 'admin') return 'Administrador';
    if (user?.role === 'coordinador') return 'Coordinador';
    return 'Solicitante';
  })();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleDownloadManual = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/manuales/mi-manual`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const fallback = user?.role === 'admin' ? 'Manual_Administrador.docx'
        : user?.role === 'coordinador' ? 'Manual_Coordinador.docx'
        : 'Manual_Solicitante.docx';
      const filename = match ? match[1] : fallback;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Manual descargado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo descargar el manual');
    }
  };

  return (
    <nav className="border-b border-[#E9ECEF] bg-white/70 backdrop-blur-xl sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#C1121F] flex items-center justify-center">
              <Truck size={24} weight="bold" className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-black font-['Chivo'] tracking-tight text-[#0A0A0A]">
                Gestión de Movilizaciones CRE JPP
              </h1>
            </div>
            {user?.role === 'admin' && (
              <div className="ml-6 flex items-center gap-1">
                <Button
                  data-testid="nav-movilizaciones"
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/admin')}
                  className={`font-['IBM_Plex_Sans'] transition-colors ${
                    location.pathname === '/admin' ? 'bg-[#F1F3F5] text-[#0A0A0A]' : 'text-[#495057] hover:bg-[#F8F9FA]'
                  }`}
                >
                  Movilizaciones
                </Button>
                <Button
                  data-testid="nav-flota"
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/flota')}
                  className={`font-['IBM_Plex_Sans'] transition-colors ${
                    location.pathname === '/flota' ? 'bg-[#F1F3F5] text-[#0A0A0A]' : 'text-[#495057] hover:bg-[#F8F9FA]'
                  }`}
                >
                  Flota
                </Button>
                <Button
                  data-testid="nav-usuarios"
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/admin/usuarios')}
                  className={`font-['IBM_Plex_Sans'] transition-colors ${
                    location.pathname === '/admin/usuarios' ? 'bg-[#F1F3F5] text-[#0A0A0A]' : 'text-[#495057] hover:bg-[#F8F9FA]'
                  }`}
                >
                  Usuarios
                </Button>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium text-[#0A0A0A] font-['IBM_Plex_Sans']">{user?.name}</p>
              <p className="text-xs text-[#495057] font-['IBM_Plex_Sans']">
                {roleLabel} - {user?.area}
              </p>
            </div>
            <Button
              data-testid="download-manual-button"
              onClick={handleDownloadManual}
              variant="outline"
              size="sm"
              className="gap-2 border-[#E9ECEF] hover:bg-[#F8F9FA] hover:border-[#0A0A0A] transition-colors"
            >
              <BookOpen size={18} weight="bold" />
              Manual
            </Button>
            <Button
              data-testid="logout-button"
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="gap-2 border-[#E9ECEF] hover:bg-[#F8F9FA] hover:border-[#0A0A0A] transition-colors"
            >
              <SignOut size={18} weight="bold" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
};